import "date-fns";
import React, { Fragment, useContext, useState, useEffect } from "react";
import { or, find, isNil, pipe, whereEq } from "ramda";
import { makeStyles } from "@material-ui/core";
import { Container, Grid } from "@material-ui/core";
import InventoryReports from "InventoryReports";
import ReportsSideBar from "ReportsSideBar";
import ReportsHeader from "ReportsHeader";
import ReportsPickerCard from "ReportsPickerCard";
import ReportsDetailsPicker from "ReportsDetailsPicker";
import NoOrganizations from "NoOrganizations";
import { UserContext } from "UserContext";
import { fetchReport, postReportRequest, saveReportResponse } from "fetchUtils";
import EmailAddressDialog from "./EmailAddressDialog";
import { daysBetween, isBlank } from "./utils";
import SuccessDialog from "./SuccessDialog";
import MarcExport from "./MarcExport";
import UsageReports from "./UsageReports";
import SessionReports from "./SessionReports";
import IapSelectReport from "./IapSelectReport";
import HelpText from "help-text/HelpText";
import KbartExport from "KbartExport";

const useStyles = makeStyles(theme => ({
  root: {
    background: theme.palette.secondary.light,
    height: "100%",
    overflowX: "hidden",
  },
  main: {
    width: "100%",
    padding: 0,
    overflowX: "hidden",
  },
  mainBody: {
    marginTop: 27,
    [theme.breakpoints.up("md")]: {
      marginTop: 0,
      paddingTop: 54,
    },
    [theme.breakpoints.up("lg")]: {
      margin: "auto",
    },
  },
  sideBarItem: {
    display: "none",
    "@media (min-width: 600px)": {
      display: "block",
    },
  },
}));

const reportTypes = [
  { id: "database-master", name: "Database Report" },
  { id: "platform-master", name: "Platform Report" },
  { id: "title-master", name: "Title Report" },
];

const counterVersions = [
  { id: "5", name: "COUNTER 5" },
  { id: "5.1", name: "COUNTER 5.1" },
];

const initialState = {
  report: reportTypes[0].id,
  startDate: new Date(),
  endDate: new Date(),
  includeMonths: false,
  isConsortiaReport: false,
  disableIncludeMonths: true,
  disableConsortiaReport: true,
  disableExport: false,
  selectedOrganization: "",
  showLoading: false,
  showEmailDialog: false,
  showSuccessDialog: false,
  currentTab: 0,
  counterVersion: "5.1",
};

const isoDate = date => date.toISOString().substr(0, 10);

const getUrl = props =>
  isBlank(props.emailAddress)
    ? getReportUrl(props)
    : getReportRequestUrl(props);

const getReportUrl = ({ report, counterVersion }) =>
  counterVersion === "5.1"
    ? `${process.env.REACT_APP_API_BASE_URL}/reports/counter/r51/${report}/report.csv`
    : `${process.env.REACT_APP_API_BASE_URL}/reports/counter/${report}/report.csv`;

const getReportRequestUrl = ({ report, counterVersion }) =>
  counterVersion === "5.1"
    ? `${process.env.REACT_APP_API_BASE_URL}/reports/counter/r51/${report}`
    : `${process.env.REACT_APP_API_BASE_URL}/reports/counter/${report}`;

const getReportUrlWithParameters = props =>
  getUrl(props).concat(
    `?org-id=${props.selectedOrganization}&start=${isoDate(
      props.startDate
    )}&end=${isoDate(props.endDate)}&includeMonths=${
      props.includeMonths
    }&isConsortiaReport=${props.isConsortiaReport}`
  );

const requestAsync = props =>
  or(
    props.isConsortiaReport,
    daysBetween(props.startDate, props.endDate) >= 90
  );

const CounterReports = () => {
  const classes = useStyles();
  const { user, token } = useContext(UserContext);
  const [state, setState] = useState(initialState);

  const handleStateChange = name => value => {
    setState(s => ({ ...s, [name]: value }));
  };

  const handleTabChange = (event, newValue) => {
    handleStateChange("currentTab")(newValue);
  };

  useEffect(() => {
    if (user.organizations.length > 0) {
      handleStateChange("selectedOrganization")(user.organizations[0].id);
    }
  }, [user.organizations]);

  useEffect(() => {
    handleStateChange("isConsortiaReport")(false);
    pipe(
      find(whereEq({ id: state.selectedOrganization, isBuyingGroup: true })),
      isNil,
      handleStateChange("disableConsortiaReport")
    )(user.organizations);
  }, [state.selectedOrganization, user.organizations]);

  useEffect(() => {
    const startDate = state.startDate;
    const endDate = state.endDate;

    if (!isNil(startDate) & !isNil(endDate)) {
      handleStateChange("disableIncludeMonths")(
        startDate.getYear() === endDate.getYear() &&
          startDate.getMonth() === endDate.getMonth()
      );

      handleStateChange("disableExport")(startDate > endDate);
    }
  }, [state.startDate, state.endDate]);

  const exportReport = props => {
    handleStateChange("showLoading")(true);

    if (requestAsync(props)) {
      handleStateChange("showEmailDialog")(true);
    } else {
      const url = getReportUrlWithParameters(props);
      console.log("%O", url);
      fetchReport(url, props.token)
        .then(response =>
          saveReportResponse(response, props.report + "-report.csv")
        )
        .finally(() => handleStateChange("showLoading")(false));
    }
  };

  const handleEmailReportRequest = props => {
    handleStateChange("showEmailDialog")(false);

    const url = getReportUrlWithParameters(props);
    console.log("%O", url);
    postReportRequest(url, props.token, props.emailAddress).finally(() => {
      handleStateChange("showLoading")(false);
      handleStateChange("showSuccessDialog")(true);
    });
  };

  return (
    <Fragment>
      <Grid container direction="row" wrap="nowrap" className={classes.root}>
        <Grid item className={classes.sideBarItem}>
          <ReportsSideBar />
        </Grid>
        <Grid item component="main" className={classes.main}>
          <ReportsHeader
            currentTab={state.currentTab}
            handleTabChange={handleTabChange}
          />
          {user.organizations.length < 1 ? (
            <NoOrganizations />
          ) : (
            <Container maxWidth="lg">
              <Grid
                container
                direction="row"
                spacing={3}
                justify="center"
                className={classes.mainBody}
              >
                <Grid item container spacing={3} xs={12} md={5} lg={6}>
                  <HelpText tab={state.currentTab} />
                </Grid>

                <Grid
                  direction="column"
                  container
                  spacing={3}
                  item
                  xs={12}
                  md={7}
                  lg={6}
                >
                  {state.currentTab === 0 && (
                    <Fragment>
                      <Grid item xs>
                        <ReportsPickerCard
                          label="ORGANIZATION"
                          values={user.organizations}
                          selectedValue={state.selectedOrganization}
                          onValueChange={handleStateChange(
                            "selectedOrganization"
                          )}
                        />
                      </Grid>
                      <Grid item xs>
                        <ReportsDetailsPicker
                          onExportClick={() =>
                            exportReport({
                              token,
                              ...state,
                            })
                          }
                          reportTypes={reportTypes}
                          counterVersions={counterVersions}
                          onVersionChange={handleStateChange("counterVersion")}
                          onReportChange={handleStateChange("report")}
                          onStartDateChange={handleStateChange("startDate")}
                          onEndDateChange={handleStateChange("endDate")}
                          onIncludeMonthsChange={handleStateChange(
                            "includeMonths"
                          )}
                          onConsortiaGroupChange={handleStateChange(
                            "isConsortiaReport"
                          )}
                          {...state}
                        />
                      </Grid>
                    </Fragment>
                  )}
                  {state.currentTab === 1 && <InventoryReports />}
                  {state.currentTab === 2 && <UsageReports />}
                  {state.currentTab === 3 && <SessionReports />}
                  {state.currentTab === 4 && <MarcExport />}
                  {state.currentTab === 5 && <KbartExport />}
                  {state.currentTab === 6 && <IapSelectReport />}
                </Grid>
              </Grid>
            </Container>
          )}
        </Grid>
      </Grid>
      <EmailAddressDialog
        open={state.showEmailDialog}
        handleCancel={() => handleStateChange("showEmailDialog")(false)}
        handleSubmit={emailAddress =>
          handleEmailReportRequest({ emailAddress, token, ...state })
        }
      />
      <SuccessDialog
        open={state.showSuccessDialog}
        message="Request for Counter Report has been sent. Check email for report download link."
        handleClose={() => handleStateChange("showSuccessDialog")(false)}
      />
    </Fragment>
  );
};

export default CounterReports;
